import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "pagination-container__page-info page-info" }
const _hoisted_2 = ["value"]
const _hoisted_3 = ["value"]
const _hoisted_4 = {
  for: "display-count",
  class: "page-info__label"
}
const _hoisted_5 = { class: "pagination-container__page-counter page-counter" }
const _hoisted_6 = { class: "page-counter__text" }
const _hoisted_7 = { class: "pagination-container__display-count display-count" }
const _hoisted_8 = {
  for: "display-count",
  class: "display-count__label"
}
const _hoisted_9 = ["value"]
const _hoisted_10 = ["value"]
const _hoisted_11 = {
  for: "display-count",
  class: "display-count__label"
}
const _hoisted_12 = { class: "pagination-container__page-move page-move" }

import { IPagingOptions } from '@/coreDomainsModule/paging';
import { computed, ref, watch } from 'vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import { onMounted } from 'vue';
import { i18n } from '@/i18n';


export default /*@__PURE__*/_defineComponent({
  __name: 'Pagination',
  props: {
    pagingOptions: {},
    isLoading: { type: Boolean },
    isInventoryPage: { type: Boolean },
    isOrderPage: { type: Boolean },
    setPageNumber: {},
    noBottomMargin: { type: Boolean }
  },
  emits: ["onChangePage"],
  setup(__props: any, { emit: __emit }) {

_useCssVars(_ctx => ({
  "af8aef22": (props.noBottomMargin ? "0px" : "16px")
}))

// n > 0 の時のみしか想定していないので、0件の時はこれ使わないこと
// TODO i18n対応
const NORMAL_PAGE_COUNT_OPTIONS = [
  { text: '5', value: 5 },
  { text: '30', value: 30 },
  { text: '50', value: 50 },
  { text: '100', value: 100 },
] as const;

const ORDER_PAGE_COUNT_OPTIONS = [
  { text: '5', value: 5 },
  { text: '30', value: 30 },
  { text: '50', value: 50 },
  { text: '100', value: 100 },
  { text: '300', value: 300 },
  { text: '500', value: 500 },
] as const;

// NOTE: 在庫管理は表示可能なLimitを増やす予定がありますが、現状BEのリクエストが死ぬので他と同じにしています
const INVENTORY_PAGE_COUNT_OPTIONS = [
  { text: '5', value: 5 },
  { text: '30', value: 30 },
  { text: '50', value: 50 },
  { text: '100', value: 100 },
] as const;

const t = i18n.t;

// props
const props = __props;

// emit
const emits = __emit;

const pageNumberOptions = computed(() => {
  return Array.from({ length: Math.ceil(pageOptions.value.totalResultsAvailable / pageOptions.value.pageSize) }, (_, i) => i + 1).map(
    (i) => ({
      text: i.toString(),
      value: i,
    })
  );
});

const pageCountOptions = computed(() => {
  if (props.isOrderPage) {
    return ORDER_PAGE_COUNT_OPTIONS;
  }
  if (props.isInventoryPage) {
    return INVENTORY_PAGE_COUNT_OPTIONS;
  }
  return NORMAL_PAGE_COUNT_OPTIONS;
});

/**
 * 初期表示のページネーションのページ数を設定
 * @param initValue - 初期表示のページネーションのページ数
 */
const initPageSize = (initValue: number): number => {
  if (initValue > 1) {
    return initValue;
  } else {
    return 30;
  }
};

const pageOptions = ref({
  offset: props.pagingOptions.offset,
  pageSize: initPageSize(props.pagingOptions.pageSize),
  totalResultsAvailable: props.pagingOptions.totalResultsAvailable ?? 1, // totalResultsAvailableがない場合はありえない(型使いまわしているせいでこうしてるだけ)
  returnCount: props.pagingOptions.returnCount,
});

const currentPage = ref(1);

/**
 * ページネーションのページ数を選択したときの処理
 * @param event - イベント(number型想定)
 */
const onSelectedPageCount = (event) => {
  currentPage.value = Number(event.target.value);
  pageOptions.value.offset = pageOptions.value.pageSize * (currentPage.value - 1);
  emits('onChangePage', pageOptions.value);
};

/**
 * ページネーションの表示件数を選択したときの処理
 * @param event - イベント(number型想定)
 */
const onSelectedPageDisplayCount = (event) => {
  pageOptions.value.pageSize = Number(event.target.value);
  currentPage.value = 1; // ページを1に戻す
  pageOptions.value.offset = 0; // offsetを0に戻す
  emits('onChangePage', pageOptions.value);
};

/**
 * ページネーションをクリックしたときの処理
 * @param isPrev - 前のページへ移動するかどうか
 */
const onChangePage = (isPrev: boolean) => {
  if (isPrev) {
    currentPage.value -= 1;
  } else {
    currentPage.value += 1;
  }
  pageOptions.value.offset = pageOptions.value.pageSize * (currentPage.value - 1);

  emits('onChangePage', pageOptions.value);
};

watch(
  () => props.pagingOptions,
  (newVal) => {
    if (newVal.offset === 0) {
      // offsetが0にリセットされた場合はページを1に戻す
      currentPage.value = 1;
    }
    pageOptions.value = {
      offset: newVal.offset,
      pageSize: initPageSize(newVal.pageSize),
      totalResultsAvailable: newVal.totalResultsAvailable ?? 1, // totalResultsAvailableがない場合はありえない(型使いまわしているせいでこうしてるだけ)
      returnCount: newVal.returnCount,
    };
  },
  { deep: true }
);

const pageCounterText = computed(() => {
  const start = pageOptions.value.pageSize * (currentPage.value - 1) + 1;
  const end = Math.min(start + pageOptions.value.pageSize - 1, pageOptions.value.totalResultsAvailable);
  return t('ページネーション表示件数', {
    start: start,
    end: end,
    total: pageOptions.value.totalResultsAvailable,
  });
});

const isDisabledPrev = computed(() => currentPage.value === 1);
const isDisabledNext = computed(() => currentPage.value >= pageNumberOptions.value.length);

watch(
  () => props.setPageNumber,
  (next) => {
    if (Number.isSafeInteger(next)) {
      currentPage.value = next as number;
    }
  },
  { immediate: true }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["pagination-container", props.isLoading ? 'pagination-container--loading' : ''])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("select", {
        value: currentPage.value,
        class: "page-info__select-box",
        onInput: _cache[0] || (_cache[0] = ($event: any) => (onSelectedPageCount($event)))
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pageNumberOptions.value, (option, index) => {
          return (_openBlock(), _createElementBlock("option", {
            key: index,
            value: option.value
          }, _toDisplayString(option.text), 9, _hoisted_3))
        }), 128))
      ], 40, _hoisted_2),
      _createElementVNode("label", _hoisted_4, _toDisplayString(_unref(t)('ページ目を表示')), 1)
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("span", _hoisted_6, _toDisplayString(pageCounterText.value), 1)
    ]),
    _createElementVNode("div", _hoisted_7, [
      _createElementVNode("label", _hoisted_8, _toDisplayString(_unref(t)('表示件数(前)')), 1),
      _createElementVNode("select", {
        value: pageOptions.value.pageSize,
        class: "display-count__select-box",
        onInput: _cache[1] || (_cache[1] = ($event: any) => (onSelectedPageDisplayCount($event)))
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pageCountOptions.value, (option, index) => {
          return (_openBlock(), _createElementBlock("option", {
            key: index,
            value: option.value
          }, _toDisplayString(option.text), 9, _hoisted_10))
        }), 128))
      ], 40, _hoisted_9),
      _createElementVNode("label", _hoisted_11, _toDisplayString(_unref(t)('表示件数(後)')), 1)
    ]),
    _createElementVNode("div", _hoisted_12, [
      _createVNode(SvgLoader, {
        disabled: isDisabledPrev.value,
        name: "arrowLeft",
        class: "page-move__left",
        onClick: _cache[2] || (_cache[2] = ($event: any) => (onChangePage(true)))
      }, null, 8, ["disabled"]),
      _createVNode(SvgLoader, {
        disabled: isDisabledNext.value,
        name: "arrowRight",
        class: "page-move__right",
        onClick: _cache[3] || (_cache[3] = ($event: any) => (onChangePage(false)))
      }, null, 8, ["disabled"])
    ])
  ], 2))
}
}

})